import React, { useState } from 'react';
import { Menu, X, Settings } from 'lucide-react';
import { BRANDS, BrandId } from '@/lib/storage';

interface NavbarProps {
  isAdmin: boolean;
  onToggleAdmin: () => void;
  activeBrand: BrandId;
  onBrandChange: (brand: BrandId) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  isAdmin,
  onToggleAdmin,
  activeBrand,
  onBrandChange,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/95 backdrop-blur-sm border-b border-dark-border"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-4">
            <button
              onClick={onToggleAdmin}
              className={`p-2 rounded-xl transition-all $
                isAdmin ? 'bg-gold/20 text-gold' : 'text-neutral-500 hover:text-gold hover:bg-neutral-900'
              }`}
              aria-label={isAdmin ? 'Exit Admin Mode' : 'Enter Admin Mode'}
              title={isAdmin ? 'Exit Admin Mode' : 'Enter Admin Mode'}
            >
              <Settings className="h-5 w-5" />
            </button>
            <a href="/" className="flex items-center gap-2" aria-label="JB Motorcycles Home">
              <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                <span className="text-gold font-black text-xl">JB</span>
              </div>
              <span className="font-black text-xl tracking-tight hidden sm:block">JB Motorcycles</span>
            </a>
          </div>

          <div className={`hidden lg:flex items-center gap-1 bg-dark-card/50 border border-dark-border rounded-2xl px-1 py-1`} role="tablist" aria-label="Motorcycle brands">
            {BRANDS.map((brand) => (
              <button
                key={brand.id}
                onClick={() => onBrandChange(brand.id)}
                className={`tab-btn ${activeBrand === brand.id ? 'tab-btn-active' : 'tab-btn-inactive'}`}
                role="tab"
                aria-selected={activeBrand === brand.id}
                aria-controls={`panel-${brand.id}`}
              >
                {brand.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 lg:gap-6">
            <button
              onClick={onToggleAdmin}
              className={`hidden lg:px-4 py-2 rounded-xl text-sm font-bold uppercase tracking-wider transition-all $
                isAdmin
                  ? 'bg-gold text-dark-bg hover:bg-gold-hover'
                  : 'bg-dark-card border border-dark-border text-neutral-300 hover:text-gold hover:border-gold/30'
              }`}
            >
              {isAdmin ? 'Admin Mode' : 'Admin Login'}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-neutral-400 hover:text-gold transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-dark-border animate-slide-up">
            <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Motorcycle brands">
              {BRANDS.map((brand) => (
                <button
                  key={brand.id}
                  onClick={() => { onBrandChange(brand.id); setMobileMenuOpen(false); }}
                  className={`tab-btn ${activeBrand === brand.id ? 'tab-btn-active' : 'tab-btn-inactive'}`}
                  role="tab"
                  aria-selected={activeBrand === brand.id}
                >
                  {brand.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-3 pt-4 border-t border-dark-border">
              <button
                onClick={onToggleAdmin}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all $
                  isAdmin ? 'bg-gold text-dark-bg' : 'bg-dark-card border border-dark-border text-neutral-300'
                }`}
              >
                <Settings className="h-4 w-4" />
                {isAdmin ? 'Exit Admin' : 'Admin Login'}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};